import React, { useState, useEffect } from 'react';
import { Button, Select } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import VideoPlayer, { Shape, DrawingTool } from '../VideoPlayer/VideoPlayer';
import { CommentType } from '../CommentSection/CommentSection';
import styles from '../../pages/projects/Projects.module.scss';

interface VideoPlayerSectionProps {
  fileDetail: any;
  versions: any[];
  versionsLoading: boolean;
  fps?: number;
  selectedCommentId?: number | null;
  
  // Main player states
  selectedVersion: number;
  currentVideoUrl: string;
  videoLoading: boolean;
  shapes: Shape[];
  drawingTool: DrawingTool;
  drawingColor: string;
  selectedShapeId: string | null;
  currentVideoTime: number;
  isPaused: boolean;
  selectedRange: [number, number] | null;
  externalRange: [number, number] | null;
  currentFrameIndex?: number;
  
  // Compare player states
  compareMode: boolean;
  activePlayer: number;
  selectedVersionCompare?: number | null;
  currentVideoUrlCompare: string;
  shapesCompare: Shape[];
  selectedShapeIdCompare: string | null;
  currentVideoTimeCompare: number;
  isPausedCompare: boolean;
  selectedRangeCompare: [number, number] | null;
  currentFrameIndexCompare?: number;
  
  // Drawing states
  isDrawingMode: boolean;
  isDrawingToolboxOpen: boolean;
  
  // Handlers for main player
  onVersionChange: (version: string) => void;
  onShapesChange: (shapes: Shape[]) => void;
  onTextToolClick: (pos: { x: number, y: number }) => void;
  onShapeSelection: (shapeId: string | null) => void;
  onDrawingComplete: () => void;
  onCurrentTimeChange: (time: number, fps: number) => void;
  onPlay: () => void;
  onSeek: () => void;
  onDurationChange: (duration: number) => void;
  onRangeSelect: (range: [number, number]) => void;
  
  // Handlers for compare player
  onCompareVersionChange: (version: string) => void;
  onShapesCompareChange: (shapes: Shape[]) => void;
  onTextToolClickCompare: (pos: { x: number, y: number }) => void;
  onShapeSelectionCompare: (shapeId: string | null) => void;
  onDrawingCompleteCompare: () => void;
  onCurrentTimeChangeCompare: (time: number, fps: number) => void;
  onPlayCompare: () => void;
  onSeekCompare: () => void;
  onDurationChangeCompare: (duration: number) => void;
  onRangeSelectCompare: (range: [number, number]) => void;
  
  // Compare mode handlers
  onCompareModeChange: (compareMode: boolean) => void;
  onActivePlayerChange: (player: number) => void;
  
  // Comment handlers
  onCommentPinClick: (commentId: number) => void;
  getFormattedComments: () => any[];
  
  // Additional callbacks for compare mode
  onCompareModeClose?: () => void;
  
  // Show resolved pins
  showResolved?: boolean;
}

const VideoPlayerSection: React.FC<VideoPlayerSectionProps> = ({
  fileDetail,
  versions,
  versionsLoading,
  fps = 30,
  selectedCommentId,
  selectedVersion,
  currentVideoUrl,
  videoLoading,
  shapes,
  drawingTool,
  drawingColor,
  selectedShapeId,
  currentVideoTime,
  isPaused,
  selectedRange,
  externalRange,
  currentFrameIndex,
  compareMode,
  activePlayer,
  selectedVersionCompare,
  currentVideoUrlCompare,
  shapesCompare,
  selectedShapeIdCompare,
  currentVideoTimeCompare,
  isPausedCompare,
  selectedRangeCompare,
  currentFrameIndexCompare,
  isDrawingMode,
  isDrawingToolboxOpen,
  onVersionChange,
  onShapesChange,
  onTextToolClick,
  onShapeSelection,
  onDrawingComplete,
  onCurrentTimeChange,
  onPlay,
  onSeek,
  onDurationChange,
  onRangeSelect,
  onCompareVersionChange,
  onShapesCompareChange,
  onTextToolClickCompare,
  onShapeSelectionCompare,
  onDrawingCompleteCompare,
  onCurrentTimeChangeCompare,
  onPlayCompare,
  onSeekCompare,
  onDurationChangeCompare,
  onRangeSelectCompare,
  onCompareModeChange,
  onActivePlayerChange,
  onCommentPinClick,
  getFormattedComments,
  onCompareModeClose,
  showResolved
}) => {
  // Compare mode değiştiğinde canvas boyutlarını güncelle
  useEffect(() => {
    if (compareMode) {
      // Compare mode'a geçişte canvas boyutlarını güncellemek için kısa bir gecikme
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [compareMode]);

  // Show resolved pin durumu - prop yoksa localStorage'dan oku
  const [showResolvedPins, setShowResolvedPins] = useState<boolean>(false);
  useEffect(() => {
    try {
      const saved = localStorage.getItem('showResolvedComments');
      setShowResolvedPins(saved === 'true');
    } catch {}
  }, []);
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'showResolvedComments') {
        setShowResolvedPins(e.newValue === 'true');
      }
    };
    const handleCustom = (e: any) => {
      if (typeof e?.detail === 'boolean') {
        setShowResolvedPins(e.detail);
      }
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('showResolvedChanged' as any, handleCustom as any);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('showResolvedChanged' as any, handleCustom as any);
    };
  }, []);

  const effectiveShowResolved = typeof showResolved === 'boolean' ? showResolved : showResolvedPins;

  // Get file type helper
  const getFileType = (fileName: string, videoUrl?: string) => {
    let extension: string | undefined = undefined;
    
    if (fileName && fileName.includes('.')) {
      const parts = fileName.split('.');
      if (parts.length > 1) {
        extension = parts.pop()?.toLowerCase();
      }
    }
    
    if ((!extension || extension === fileName.toLowerCase()) && videoUrl) {
      const cleanUrl = videoUrl.split('?')[0].split('#')[0];
      const urlExtension = cleanUrl.split('.').pop()?.toLowerCase().trim();
      if (urlExtension && urlExtension.length <= 4) {
        const cleanExtension = urlExtension.replace(/[^a-z0-9]/gi, '');
        if (cleanExtension) {
          extension = cleanExtension;
        }
      }
    }
    
    let result = 'other';
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension || '')) {
      result = 'video';
    } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'ico'].includes(extension || '')) {
      result = 'image';
    } else if (['pdf'].includes(extension || '')) {
      result = 'pdf';
    } else if (['doc', 'docx'].includes(extension || '')) {
      result = 'document';
    }
    
    return result;
  };

  // Get version options
  const getVersionOptions = () => {
    if (!fileDetail?.versions) return [];
    
    return fileDetail.versions.map((version: any) => ({
      label: version.name,
      value: version.name, // ID yerine name kullan
      disabled: false
    }));
  };

  const versionOptions = getVersionOptions();

  // Handle version selection with URL hash update
  const handleVersionSelect = (value: string, isCompare = false) => {
    // Versiyon name'inden ID'yi bul
    const selectedVersionData = fileDetail.versions?.find((v: any) => v.name === value);
    const versionId = selectedVersionData?.id;
    
    if (isCompare) {
      onCompareVersionChange(versionId);
    } else {
      onVersionChange(versionId);
      // Update URL hash for main player
      if (selectedVersionData?.name) {
        window.location.hash = selectedVersionData.name;
      } else if (selectedVersionData?.path) {
        const pathParts = selectedVersionData.path.split('/');
        const versionName = pathParts[pathParts.length - 1];
        if (versionName) {
          window.location.hash = versionName;
        }
      }
    }
  };

  // Render file viewer for main player
  const renderFileViewer = () => {
    if (!fileDetail) {
      return null;
    }

    const fileUrl = currentVideoUrl || 
                   fileDetail?.versions?.[0]?.fileUrl || 
                   fileDetail.url || 
                   fileDetail.fileUrl || 
                   fileDetail.downloadUrl || 
                   fileDetail.path || '';
    
    const fileType = getFileType(fileDetail.name, fileUrl);
    
    if (fileType === 'video') {
      if (videoLoading) {
        return (
          <div className={styles.loadingContainer}>
            <h3>{fileDetail.name}</h3>
            <p>Video yükleniyor...</p>
          </div>
        );
      }
      
      if (!fileUrl) {
        return (
          <div className={styles.errorContainer}>
            <h3>{fileDetail.name}</h3>
            <p>Video URL'i bulunamadı</p>
          </div>
        );
      }
      
      return (
        <VideoPlayer
          key={`${fileDetail.id}-${selectedVersion}`}
          src={fileUrl}
          fileType={fileType}
          poster={fileDetail.thumbnailUrl}
          isDrawingEnabled={isDrawingMode}
          title={fileDetail.name}
          shapes={shapes}
          tool={drawingTool}
          color={drawingColor}
          onShapesChange={onShapesChange}
          onTextToolClick={onTextToolClick}
          selectedShapeId={selectedShapeId}
          onShapeSelection={onShapeSelection}
          onDrawingComplete={onDrawingComplete}
          isDrawingToolboxOpen={isDrawingToolboxOpen}
          onCurrentTimeChange={onCurrentTimeChange}
          currentVideoTime={currentVideoTime}
          isPaused={isPaused}
          onPlay={onPlay}
          onSeek={onSeek}
          onDurationChange={onDurationChange}
          selectedRange={selectedRange}
          onRangeSelect={onRangeSelect}
          externalRange={externalRange}
          currentFrameIndex={currentFrameIndex}
          comments={getFormattedComments()}
          onCommentClick={onCommentPinClick}
          selectedCommentId={selectedCommentId}
          fps={fps}
          compareMode={compareMode}
          showResolved={effectiveShowResolved}
        />
      );
    } else if (fileType === 'image') {
      if (!fileUrl) {
        return (
          <div className={styles.errorContainer}>
            <h3>{fileDetail.name}</h3>
            <p>Image URL'i bulunamadı</p>
          </div>
        );
      }
      
      return (
        <div className={styles.imageContainer}>
          <VideoPlayer
            key={`${fileDetail.id}-${selectedVersion}`}
            src={fileUrl}
            fileType={fileType}
            poster={fileDetail.thumbnailUrl}
            isDrawingEnabled={isDrawingMode}
            title={fileDetail.name}
            shapes={shapes}
            tool={drawingTool}
            color={drawingColor}
            onShapesChange={onShapesChange}
            onTextToolClick={onTextToolClick}
            selectedShapeId={selectedShapeId}
            onShapeSelection={onShapeSelection}
            onDrawingComplete={onDrawingComplete}
            isDrawingToolboxOpen={isDrawingToolboxOpen}
            onCurrentTimeChange={onCurrentTimeChange}
            currentVideoTime={currentVideoTime}
            isPaused={isPaused}
            onPlay={onPlay}
            onSeek={onSeek}
            onDurationChange={onDurationChange}
            selectedRange={selectedRange}
            onRangeSelect={onRangeSelect}
            externalRange={externalRange}
            currentFrameIndex={currentFrameIndex}
            comments={getFormattedComments()}
            onCommentClick={onCommentPinClick}
            selectedCommentId={selectedCommentId}
            fps={fps}
            compareMode={compareMode}
            showResolved={effectiveShowResolved}
          />
        </div>
      );
    } else {
      return (
        <div className={styles.otherFileContainer}>
          <h3>{fileDetail.name}</h3>
          <p>Dosya tipi: {fileType}</p>
          {fileDetail.thumbnailUrl && (
            <img 
              src={fileDetail.thumbnailUrl} 
              alt={fileDetail.name}
              className={styles.otherFileThumbnail}
            />
          )}
        </div>
      );
    }
  };

  // Render compare player content
  const renderComparePlayer = () => {
    if (!selectedVersionCompare || !currentVideoUrlCompare) {
      return (
        <div className={styles.versionPlaceholder}>
          <div>
            <div className={styles.placeholderTitle}>Select Version</div>
            <div className={styles.placeholderSubtitle}>Choose a version for comparison</div>
          </div>
        </div>
      );
    }

    const compareFileType = getFileType(fileDetail?.name, currentVideoUrlCompare);
    
    if (compareFileType === 'video') {
      return (
        <VideoPlayer
          key={`compare-${fileDetail.id}-${selectedVersionCompare}`}
          src={currentVideoUrlCompare}
          poster={fileDetail.thumbnailUrl}
          isDrawingEnabled={isDrawingMode && activePlayer === 2}
          title={fileDetail.name + ' (Compare)'}
          shapes={shapesCompare}
          tool={drawingTool}
          color={drawingColor}
          onShapesChange={onShapesCompareChange}
          onTextToolClick={onTextToolClickCompare}
          selectedShapeId={selectedShapeIdCompare}
          onShapeSelection={onShapeSelectionCompare}
          onDrawingComplete={onDrawingCompleteCompare}
          onCurrentTimeChange={onCurrentTimeChangeCompare}
          currentVideoTime={currentVideoTimeCompare}
          isPaused={isPausedCompare}
          onPlay={onPlayCompare}
          onSeek={onSeekCompare}
          onDurationChange={onDurationChangeCompare}
          selectedRange={selectedRangeCompare}
          onRangeSelect={onRangeSelectCompare}
          externalRange={null}
          currentFrameIndex={currentFrameIndexCompare}
          comments={getFormattedComments()}
          onCommentClick={onCommentPinClick}
          selectedCommentId={selectedCommentId}
          fps={fps}
          compareMode={compareMode}
          showResolved={effectiveShowResolved}
        />
      );
    } else if (compareFileType === 'image') {
      return (
        <div className={styles.compareImageContainer}>
          <img
            key={`compare-img-${fileDetail.id}-${selectedVersionCompare}`}
            src={currentVideoUrlCompare}
            alt="Compared Image"
            className={styles.compareImage}
          />
        </div>
      );
    } else {
      return (
        <div className={styles.compareOtherContainer}>
          Bu dosya türü desteklenmiyor
        </div>
      );
    }
  };

  return (
    <div className={styles.fileViewer}>
      {/* Close button for compare mode */}
      {compareMode && (
        <div className={styles.compareCloseButtonContainer}>
        <Button
          icon={<CloseOutlined />}
          size="small"
          className={styles.compareCloseButton}
          onClick={() => {
            onCompareModeChange(false);
            // Ek callback'i çağır
            onCompareModeClose?.();
          }}
        />
        </div>
      )}
      
      <div className={`${styles.videoContainer} ${compareMode ? '' : styles.singleMode}`}>
        {/* Main Player */}
        <div 
          className={`${styles.playerWrapper} ${compareMode ? styles.compareMode : ''} ${compareMode && activePlayer === 1 && selectedVersion ? styles.active : ''} ${compareMode && selectedVersion ? styles.hasVersion : ''} ${compareMode && !selectedVersion ? styles.noVersion : ''}`}
          onClick={compareMode && selectedVersion ? () => onActivePlayerChange(1) : undefined}
        >
          {compareMode && (
            <div className={styles.playerHeader}>
              <div
                className={`${styles.radioButton} ${activePlayer === 1 && selectedVersion ? styles.active : ''} ${selectedVersion ? styles.clickable : ''}`}
                onClick={selectedVersion ? (e) => {
                  e.stopPropagation();
                  onActivePlayerChange(1);
                } : undefined}
              >
                {activePlayer === 1 && (
                  <div className={styles.radioDot} />
                )}
              </div>
              <span className={styles.versionLabel}>
                {(() => {
                  if (selectedVersion && versions.length > 0) {
                    const selectedVersionData = versions.find((v: any) => v.id === selectedVersion);
                    const versionName = selectedVersionData?.name || `Version ${selectedVersion}`;
                    return `${fileDetail.name} - ${versionName}`;
                  }
                  return 'Select Version';
                })()}
              </span>
            </div>
          )}
          
          {/* Version selector for main player - only visible in compare mode */}
          {compareMode && (
            <div className={`${styles.versionSelectWrapper} ${compareMode ? styles.compareMode : ''}`}>
              <Select
                placeholder="Select version"
                value={(() => {
                  if (selectedVersion && versions.length > 0) {
                    const selectedVersionData = versions.find((v: any) => v.id === selectedVersion);
                    return selectedVersionData?.name || selectedVersion;
                  }
                  return undefined;
                })()}
                onChange={(value) => handleVersionSelect(value, false)}
                loading={versionsLoading}
                style={{ width: '100%' }}
                options={versionOptions}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          
          {/* File Viewer Container */}
          <div className={`${styles.videoPlayerContainer} ${compareMode ? styles.compareMode : ''}`}>
            {renderFileViewer()}
          </div>
        </div>
        
        {/* Compare Player */}
        {compareMode && (
          <div className={styles.comparePlayerContainer}>
            <div 
              className={`${styles.playerWrapper} ${styles.compareMode} ${activePlayer === 2 && selectedVersionCompare ? styles.active : ''} ${selectedVersionCompare ? styles.hasVersion : ''} ${!selectedVersionCompare ? styles.noVersion : ''}`}
              onClick={selectedVersionCompare ? () => onActivePlayerChange(2) : undefined}
            >
              <div className={styles.playerHeader}>
                <div
                  className={`${styles.radioButton} ${activePlayer === 2 && selectedVersionCompare ? styles.active : ''} ${selectedVersionCompare ? styles.clickable : ''}`}
                  onClick={selectedVersionCompare ? (e) => {
                    e.stopPropagation();
                    onActivePlayerChange(2);
                  } : undefined}
                >
                  {activePlayer === 2 && (
                    <div className={styles.radioDot} />
                  )}
                </div>
                <span className={styles.versionLabel}>
                  {(() => {
                    if (selectedVersionCompare && versions.length > 0) {
                      const selectedVersionData = versions.find((v: any) => v.id === selectedVersionCompare);
                      const versionName = selectedVersionData?.name || `Version ${selectedVersionCompare}`;
                      return `${fileDetail.name} - ${versionName}`;
                    }
                    return 'Select Version';
                  })()}
                </span>
              </div>
              
              {/* Version selector for compare player */}
              <div className={`${styles.versionSelectWrapper} ${styles.compareMode}`}>
                <Select
                  placeholder="Select version"
                  value={(() => {
                    if (selectedVersionCompare && versions.length > 0) {
                      const selectedVersionData = versions.find((v: any) => v.id === selectedVersionCompare);
                      return selectedVersionData?.name || selectedVersionCompare;
                    }
                    return undefined;
                  })()}
                  onChange={(value) => handleVersionSelect(value, true)}
                  loading={versionsLoading}
                  style={{ width: '100%' }}
                  options={versionOptions}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              
              <div className={`${styles.videoPlayerContainer} ${styles.compareMode}`}>
                {renderComparePlayer()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlayerSection; 